import {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import Swal from 'sweetalert2'
import {updateFranchiseStar} from '../core/_request'
import {TStarRequest, TStarResponse} from '../../../coreGlobal/models/franchise/franchise'
import {formatMask, formatMonetaryToBr} from '../../../utils/formatMonetaryToBr'
import {useFormik} from 'formik'
import {franchiseStarSchema} from '../core/validation/FanchiseVali'
import {buildUpdateStarRequest} from '../core/buildUpdateStarRequest'

type TStarProps = {
  stars: TStarResponse
}

export const StarForm = (props: TStarProps) => {
  const [loading, setLoading] = useState(false)
  let {id} = useParams()

  const formik = useFormik<TStarResponse>({
    initialValues: [
      {description: '', starsCount: 0, minAmount: '0', maxAmount: '0', observation: ''},
    ],
    validationSchema: franchiseStarSchema,
    onSubmit: async (values) => {
      const valuesFilter = await buildUpdateStarRequest(values)
      submit(valuesFilter)
    },
  })

  useEffect(() => {
    if (props.stars.length === 0) return

    setLoading(true)

    const values = props.stars.map((field) => ({
      description: field?.description,
      starsCount: field?.starsCount,
      minAmount: formatMonetaryToBr(field?.minAmount),
      maxAmount: formatMonetaryToBr(field?.maxAmount),
      observation: field.observation,
    }))

    formik.setValues(values)

    setLoading(false)
  }, [props.stars])

  const handleOnChangeAmount =
    (index: number, variable: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      formik.setFieldValue(`[${index}].${variable}`, formatMask(event.target.value))
    }

  const submit = async (valuesFilter: TStarRequest[]) => {
    Swal.fire({
      title: 'Deseja alterar os valores da estrela?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-danger', // Classe personalizada para o botão de confirmação
        cancelButton: 'btn btn-secondary', // Classe personalizada para o botão de cancelamento (opcional)
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true)
        try {
          await updateFranchiseStar(String(id), valuesFilter)
          await Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'Salvo com sucesso',
            showConfirmButton: false,
            timer: 1500,
          })
        } catch (error: any) {
          await Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: error.response?.data?.message,
          })
        } finally {
          setLoading(false)
        }
      }
    })
  }

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <div className='card card-custom mb-5 mb-xl-10'>
          <div className='card-header'>
            <h3 className='card-title fw-bolder m-0'>Estrelas</h3>
            <div className='card-toolbar'></div>
          </div>
          <div className='card-body d-flex overflow-auto'>
            <table className='table table-row-dashed table-row-gray-300 align-middle'>
              <thead>
                <tr className='fw-bolder fs-6 text-gray-800'>
                  <th style={{minWidth: '50px'}}>Número</th>
                  <th style={{minWidth: '150px'}}>Nome</th>
                  <th style={{minWidth: '150px'}}>Valor Mínimo</th>
                  <th style={{minWidth: '150px'}}>Valor Máximo</th>
                  <th style={{minWidth: '250px'}}>Obs</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(props.stars) &&
                  props.stars?.map((field, index) => (
                    <tr key={`stars${index}`}>
                      <td className='text-center'> {formik.values?.[index]?.starsCount}</td>
                      <td>
                        <div className='column col-lg-12'>
                          <input
                            type='text'
                            maxLength={255}
                            className='form-control form-control-lg form-control-solid'
                            placeholder='Nome'
                            {...formik.getFieldProps(`${index}.description`)}
                          />
                          {formik.errors?.[index]?.description && (
                            <div className='text-danger'>
                              {formik.errors[index]?.description as string}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className='column col-lg-12'>
                          <input
                            type='text'
                            maxLength={14}
                            className='form-control form-control-lg form-control-solid'
                            placeholder='Valor mínimo'
                            {...formik.getFieldProps(`${index}.minAmount`)}
                            onChange={handleOnChangeAmount(index, 'minAmount')}
                          />
                          {formik.errors?.[index]?.minAmount && (
                            <div className='text-danger'>
                              {formik.errors[index]?.minAmount as string}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className='column col-lg-12'>
                          <input
                            type='text'
                            maxLength={14}
                            className='form-control form-control-lg form-control-solid'
                            placeholder='Valor máximo'
                            {...formik.getFieldProps(`${index}.maxAmount`)}
                            onChange={handleOnChangeAmount(index, 'maxAmount')}
                          />
                          {formik.errors?.[index]?.maxAmount && (
                            <div className='text-danger'>
                              {formik.errors[index]?.maxAmount as string}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <textarea
                          maxLength={255}
                          className='form-control form-control-lg form-control-solid'
                          placeholder='Observação'
                          {...formik.getFieldProps(`${index}.observation`)}
                        />
                        {formik.errors?.[index]?.observation && (
                          <div className='text-danger'>
                            {formik.errors[index]?.observation as string}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            <button type='submit' className='btn btn-danger' disabled={loading || !formik.isValid}>
              {!loading && 'Salvar'}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Salvando...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </div>
      </form>
    </>
  )
}
